import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';

// Pages
import { SignInPageComponent } from '@app/pages/sign-in-page/sign-in-page.component';
import { RegisterPageComponent } from '@app/pages/register-page/register-page.component';
import { LandingPageComponent } from '@app/pages/landing-page/landing-page.component';
import { MyProfilePageComponent } from '@app/pages/my-profile-page/my-profile-page.component';
import { DogProfilePageComponent } from '@app/pages/dog-profile-page/dog-profile-page.component';
import { BasicInfoComponent } from '@app/components/register/basic-info/basic-info.component';
import { SignInFormComponent } from '@app/components/sign-in/sign-in-form/sign-in-form.component';
import { ChangePasswordPageComponent } from '@app/pages/change-password-page/change-password-page.component';
import { InterviewRequestPageComponent } from '@app/pages/interview-request-page/interview-request-page.component';
import { BookingsPageComponent } from '@app/pages/bookings-page/bookings-page.component';
import { BookingsDetailsPageComponent } from '@app/pages/bookings-page/bookings-details-page/bookings-details-page.component';
import { WelcomePageComponent } from '@app/pages/welcome-page/welcome-page.component';
import { OneFactorAuthComponent } from '@app/components/register/one-factor-auth/one-factor-auth.component';
import { LocationSelectionComponent } from '@app/components/register/location-selection/location-selection.component';
import { SignInBasicInfoComponent } from '@app/components/sign-in/sign-in-basic-info/sign-in-basic-info.component';
import { ConfirmationPageComponent } from '@app/pages/register-page/confirmation-page/confirmation-page.component';
import { ForgotPasswordPageComponent } from '@app/pages/forgot-password-page/forgot-password-page.component';
import { PreferencesPageComponent } from '@app/pages/preferences-page/preferences-page.component';
import { ReservationPageComponent } from '@app/pages/reservation-page/reservation-page.component';
import { CamperCamsPageComponent } from '@app/pages/camper-cams-page/camper-cams-page.component';
import { AddCamperPageComponent } from '@app/pages/add-camper-page/add-camper-page.component';
import { PaymentListPageComponent } from '@app/pages/payment-list-page/payment-list-page.component';
import { PaymentEditPageComponent } from '@app/pages/payment-edit-page/payment-edit-page.component';
import { PackagePageComponent } from '@app/pages/package-page/package-page.component';
import { CamperListPageComponent } from '@app/pages/camper-list-page/camper-list-page.component';

// Guards
import { AuthGuard } from '@app/auth/auth.guard';
import { AnonymousGuard } from '@app/auth/anonymous.guard';
import { CanDeactivateGuard } from '@app/guards/can-deactivate.guard';

// Resolvers
import { PetInfoResolver } from '@app/resolvers/pet-info.resolver';
import { PreferredLocationResolver } from '@app/resolvers/preferred-location.resolver';
import { PaymentCardInfoResolver } from '@app/resolvers/payment-card-info.resolver';

const routes: Routes = [
  {
    path: 'welcome',
    component: WelcomePageComponent,
    canActivate: [AnonymousGuard],
  },
  {
    path: 'sign-in',
    component: SignInPageComponent,
    canActivate: [AnonymousGuard],
    children: [
      {
        path: '',
        component: SignInFormComponent,
      },
      {
        path: 'info',
        component: SignInBasicInfoComponent,
      },
    ],
  },
  {
    path: 'register',
    component: RegisterPageComponent,
    canActivate: [AnonymousGuard],
    children: [
      {
        path: '',
        component: BasicInfoComponent,
      },
      {
        path: 'code',
        component: OneFactorAuthComponent,
      },
      {
        path: 'location',
        component: LocationSelectionComponent,
      },
    ],
  },
  {
    path: 'register/confirm',
    component: ConfirmationPageComponent,
    canActivate: [AnonymousGuard],
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordPageComponent,
    canActivate: [AnonymousGuard],
  },
  {
    path: '',
    component: LandingPageComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'my-profile',
    component: MyProfilePageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [CanDeactivateGuard],
  },
  {
    path: 'camper-profile/add',
    component: AddCamperPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [CanDeactivateGuard],
  },
  {
    path: 'camper-profile/:id',
    component: DogProfilePageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [CanDeactivateGuard],
    resolve: { petInfo: PetInfoResolver },
  },
  // {
  //   path: 'camper-list',
  //   component: CamperListPageComponent,
  //   canActivate: [AuthGuard],
  //   canDeactivate: [CanDeactivateGuard],
  // },
  {
    path: 'preferences',
    component: PreferencesPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [CanDeactivateGuard],
  },
  {
    path: 'change-password',
    component: ChangePasswordPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [CanDeactivateGuard],
  },
  {
    path: 'camper-cams',
    component: CamperCamsPageComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'interview-request',
    component: InterviewRequestPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [CanDeactivateGuard],
  },
  {
    path: 'reservation',
    component: ReservationPageComponent,
    canActivate: [AuthGuard],
    canDeactivate: [CanDeactivateGuard],
  },
  {
    path: 'upcoming-bookings',
    component: BookingsPageComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'upcoming-bookings/:id',
    component: BookingsDetailsPageComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'payments',
    component: PaymentListPageComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'payments/add',
    component: PaymentEditPageComponent,
    canActivate: [AuthGuard],
    resolve: { preferredLocation: PreferredLocationResolver },
  },
  {
    path: 'payments/:id',
    component: PaymentEditPageComponent,
    canActivate: [AuthGuard],
    resolve: {
      preferredLocation: PreferredLocationResolver,
      paymentCard: PaymentCardInfoResolver,
    },
  },
  {
    path: 'packages',
    component: PackagePageComponent,
    canActivate: [AuthGuard],
  },
  {
    path: '**',
    redirectTo: '',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      relativeLinkResolution: 'legacy',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
